import { runAsyncRoutine } from '@thinkalpha/ui-core/routine/index';
import type { Widget, WidgetContainer, WidgetTab, Workspace } from '@thinkalpha/ui-types/contracts/workspace';
import type { LegacyContractWidget } from 'src/lib/widget';
import { widgetToViewModel } from 'src/lib/widget';
import { normalizeLegacyAlphaEyeTab } from 'src/lib/widget/alphaEyeCompatibility';
import { allWidgets } from 'src/lib/widget/widgets';
import { selfOwnedWidgetRegistry } from 'src/services/WorkspaceService/selfOwnedWidgetRegistry';
import { container } from 'src/StaticContainer';
import type { SelfOwnedWidgetViewModel, WidgetTabViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';

const log = container.get('Logger').getSubLogger({ name: 'generateWidgetTabViewModels' });

function isLegacyContractWidget(widget: Widget): widget is LegacyContractWidget {
    return !selfOwnedWidgetRegistry.has(widget.type);
}

function selfOwnedMarker(widget: Widget): SelfOwnedWidgetViewModel {
    return { type: widget.type, channelId: widget.channelId ?? null } as SelfOwnedWidgetViewModel;
}

export async function generateWidgetTabViewModels(
    workspace: Workspace,
    queryClient: ReactBindings['QueryClient'],
): Promise<WidgetTabViewModel[]> {
    const results = await Promise.all(
        workspace.containers.flatMap((container) => {
            const activeTabId = container.activeTabId ?? container.tabs[0]?.id;

            return container.tabs.map(async (tab: WidgetTab): Promise<WidgetTabViewModel> => {
                if (isLegacyContractWidget(tab.widget)) {
                    const widget = await runAsyncRoutine(widgetToViewModel, { QueryClient: queryClient }, tab.widget);
                    if (widget === null) {
                        throw new Error(
                            `Unexpected null widget in cleaned workspace: workspaceId=${workspace.id}, tabId=${tab.id}, widgetType=${tab.widget.type}`,
                        );
                    }
                    return {
                        id: tab.id,
                        name: tab.name,
                        widget,
                        isActive: activeTabId === tab.id,
                        containerId: container.id,
                        channelId: tab.widget.channelId || null,
                    };
                }

                return {
                    id: tab.id,
                    name: tab.name,
                    widget: selfOwnedMarker(tab.widget),
                    isActive: activeTabId === tab.id,
                    containerId: container.id,
                    channelId: tab.widget.channelId || null,
                };
            });
        }),
    );
    return results;
}

/**
 * Strips unrecognized widget tabs from a workspace and removes any containers that become empty.
 * Returns a new workspace with only valid widgets.
 */
export function stripUnrecognizedWidgets(workspace: Workspace): Workspace {
    const filteredContainers: WidgetContainer[] = [];

    for (const container of workspace.containers) {
        const validTabs: WidgetTab[] = [];

        if (TRACE) {
            log.trace('Processing container', {
                workspaceId: workspace.id,
                containerId: container.id,
                tabCount: container.tabs.length,
            });
        }

        for (const rawTab of container.tabs) {
            const tab = normalizeLegacyAlphaEyeTab(rawTab);
            if (TRACE) {
                log.trace('Validating widget', {
                    workspaceId: workspace.id,
                    containerId: container.id,
                    tabId: tab.id,
                    widgetType: tab.widget.type,
                });
            }
            const isRecognized = tab.widget.type in allWidgets || (tab.widget.type as string) === 'order-entry';
            if (isRecognized) {
                validTabs.push(tab);
            } else {
                log.warn('Stripping unrecognized widget from workspace', {
                    workspaceId: workspace.id,
                    widgetType: tab.widget.type,
                    tabId: tab.id,
                    containerId: container.id,
                });
            }
        }

        // Only include the container if it has at least one valid tab
        if (validTabs.length > 0) {
            // Update activeTabId if it was pointing to a stripped tab
            const activeTabId =
                container.activeTabId && validTabs.some((t) => t.id === container.activeTabId)
                    ? container.activeTabId
                    : (validTabs[0]?.id ?? null);

            filteredContainers.push({
                ...container,
                tabs: validTabs,
                activeTabId,
            });
        } else {
            log.warn('Removing empty container from workspace after stripping unrecognized widgets', {
                workspaceId: workspace.id,
                containerId: container.id,
            });
        }
    }

    return {
        ...workspace,
        containers: filteredContainers,
    };
}
